import { useEffect, useState } from "react";

import {
  Link,
  useParams,
  useSearchParams
} from "react-router-dom";

import axios from "axios";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import multiMonthPlugin from "@fullcalendar/multimonth";

import ArtistBookingModal from "./ArtistBookingModal";

import { useAuth } from "../../context/AuthContext";


function Bookings() {
  const { artistId } = useParams();
  const [searchParams] = useSearchParams();

  const currentPage =
    Number(searchParams.get("page")) || 1;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showBookingModal, setShowBookingModal] =
    useState(false);

  const [selectedDates, setSelectedDates] =
    useState(null);

  const [selectedBooking, setSelectedBooking] =
    useState(null);

  const [activeTab, setActiveTab] =
    useState("calendar");

  const token =
    sessionStorage.getItem("JWT_TOKEN");

  
  const { role } = useAuth();

  const canManageBookings = [
    "admin",
    "artist",
    "agent",
    "organization"
  ].includes(role);


  /*
   * Fetch artist bookings
   */
  const fetchBookings = async () => {
    if (!artistId) {
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/bookings/${artistId}`,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + token
          }
        }
      );

      const bookings =
        response.data?.documents || [];

      const calendarEvents = bookings.map(
        (booking) => ({
          id: booking.$id,
          title: booking.title,
          start: booking.startDate,
          end: booking.endDate,
          allDay: true,

          extendedProps: {
            artistId: booking.artistId,
            description:
              booking.description || "",
            status:
              booking.status || "confirmed"
          }
        })
      );

      setEvents(calendarEvents);

    } catch (error) {
      console.error(
        "Failed to fetch bookings:",
        error
      );

      setError(
        error.response?.data?.message ||
        "Failed to load artist bookings."
      );

    } finally {
      setLoading(false);
    }
  };


  /*
   * Load bookings when artist changes
   */
  useEffect(() => {
    fetchBookings();
  }, [artistId]);


  /*
   * Automatically scroll Year view to
   * the current month.
   */
  const scrollToCurrentMonth = () => {
    setTimeout(() => {
      const today = new Date();

      const year = today.getFullYear();
      const month = String(
        today.getMonth() + 1
      ).padStart(2, "0");

      const todayDate =
        `${year}-${month}-01`;

      const currentMonthElement =
        document.querySelector(
          `.fc-multimonth-month[data-date="${todayDate}"]`
        );

      /*
       * FullCalendar versions can render the
       * month element without the data-date
       * attribute, so use the month title as
       * a fallback.
       */
      let target =
        currentMonthElement;

      if (!target) {
        const monthTitles =
          document.querySelectorAll(
            ".fc-multimonth-title"
          );

        const currentMonthName =
          today.toLocaleString("default", {
            month: "long"
          });

        target =
          Array.from(monthTitles).find(
            (element) =>
              element.textContent
                ?.trim()
                .startsWith(
                  currentMonthName
                )
          );
      }

      if (!target) {
        return;
      }

      let scrollContainer =
        target.parentElement;

      while (
        scrollContainer &&
        scrollContainer !== document.body
      ) {
        const style =
          window.getComputedStyle(
            scrollContainer
          );

        const isScrollable =
          (
            style.overflowY === "auto" ||
            style.overflowY === "scroll"
          ) &&
          scrollContainer.scrollHeight >
          scrollContainer.clientHeight;

        if (isScrollable) {
          break;
        }

        scrollContainer =
          scrollContainer.parentElement;
      }

      if (
        !scrollContainer ||
        scrollContainer === document.body
      ) {
        target.scrollIntoView({
          behavior: "auto",
          block: "start"
        });

        return;
      }

      const targetTop =
        target.getBoundingClientRect().top;

      const containerTop =
        scrollContainer
          .getBoundingClientRect()
          .top;

      scrollContainer.scrollTo({
        top:
          scrollContainer.scrollTop +
          (targetTop - containerTop) -
          15,
        behavior: "auto"
      });

    }, 300);
  };


  /*
   * Calendar date selection
   * Used for ADD booking
   */
  const handleDateSelect = (info) => {
    setSelectedBooking(null);

    setSelectedDates({
      start: info.start,
      end: info.end,
      allDay: info.allDay
    });

    setShowBookingModal(true);
  };


  const handleDateClick = (info) => {
    setSelectedBooking(null);
    const clickedDate = new Date(info.date);
    const endDate = new Date(clickedDate);

    endDate.setDate(
      endDate.getDate() + 1
    );

    setSelectedDates({
      start: clickedDate,
      end: endDate,
      allDay: true
    });

    setShowBookingModal(true);
  };


  /*
   * Existing booking click
   * Used for EDIT / DELETE booking
   */
  const handleEventClick = (info) => {
    const event = info.event;

    setSelectedDates(null);

    setSelectedBooking({
      id: event.id,
      title: event.title,
      startDate: event.start,
      endDate: event.end,

      description:
        event.extendedProps?.description ||
        "",

      status:
        event.extendedProps?.status ||
        "confirmed",

      artistId:
        event.extendedProps?.artistId ||
        artistId
    });

    setShowBookingModal(true);
  };


  /*
   * Booking saved
   */
  const handleBookingSaved = async () => {
    await fetchBookings();
  };


  /*
   * Close modal
   */
  const handleCloseModal = () => {
    setShowBookingModal(false);
    setSelectedDates(null);
    setSelectedBooking(null);
  };


  return (
    <>
      <style>{`
        .bookings-calendar {
          width: 100%;
          height: calc(100vh - 300px);
          min-height: 430px;
          max-height: 700px;
          overflow: hidden;
        }

        .bookings-calendar .fc {
          width: 100%;
          height: 100%;
        }

        .bookings-calendar .fc-view-harness {
          height: 100% !important;
        }

        .bookings-calendar .fc-daygrid-body {
          width: 100% !important;
        }

        .bookings-calendar .fc-daygrid-body table {
          width: 100% !important;
        }

        .bookings-calendar .fc-scrollgrid {
          width: 100% !important;
        }

        .bookings-calendar .fc-daygrid-day {
          min-width: 0;
        }

        /*
         * Month view:
         * keep the same compact date-box size
         * as the current screenshot.
         */
        .bookings-calendar .fc-daygrid-day-frame {
          min-height: 0;
          height: 100%;
          padding: 4px;
        }

        .bookings-calendar .fc-daygrid-day-number {
          font-size: 13px;
          font-weight: 500;
          padding: 5px;
        }

        .bookings-calendar .fc-col-header-cell-cushion {
          font-size: 13px;
          font-weight: 600;
          padding: 7px 4px;
        }

        /*
         * Current date
         */
        .bookings-calendar .fc-day-today {
          background: rgba(59, 130, 246, 0.10) !important;
        }

        .bookings-calendar
          .fc-day-today
          .fc-daygrid-day-number {
          background: #3b82f6;
          color: #fff;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 3px;
        }

        /*
         * Booking event
         */
        .bookings-calendar .fc-custom-event {
          padding: 3px 5px;
          border-radius: 4px;
          overflow: hidden;
          cursor: pointer;
        }

        .bookings-calendar .fc-custom-event-title {
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .bookings-calendar .fc-custom-event-description {
          font-size: 11px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /*
         * Booking status
         */
        .bookings-calendar .status-confirmed {
          background: #f6a400;
          color: #fff;
        }

        .bookings-calendar .status-pending {
          background: #6c757d;
          color: #fff;
        }

        .bookings-calendar .status-cancelled {
          background: #dc3545;
          color: #fff;
        }

        /*
         * Calendar toolbar
         */
        .bookings-calendar .fc-toolbar {
          margin-bottom: 12px;
        }

        .bookings-calendar .fc-toolbar-title {
          font-size: 20px;
          font-weight: 600;
        }

        .bookings-calendar .fc-button {
          padding: 5px 10px;
          font-size: 13px;
        }

        /*
         * Year view
         */
        .bookings-calendar .fc-multimonth {
          overflow-y: auto;
          overflow-x: hidden;
        }

        .bookings-calendar .fc-multimonth-month {
          margin-bottom: 18px;
        }

        .bookings-calendar .fc-multimonth-title {
          font-size: 16px;
          font-weight: 600;
          padding: 8px 0;
        }

        .bookings-calendar
          .fc-multimonth-month
          .fc-daygrid-day {
          min-height: 55px;
        }

        .bookings-calendar
          .fc-multimonth-month
          .fc-daygrid-day-frame {
          min-height: 55px;
        }

        .bookings-calendar
          .fc-multimonth-month
          .fc-daygrid-day-number {
          font-size: 11px;
          padding: 4px;
        }

        /*
         * Year view current day
         */
        .bookings-calendar
          .fc-multimonth-month
          .fc-day-today {
          background: rgba(59, 130, 246, 0.10) !important;
        }

        /*
         * Tabs
         */
        .booking-tabs {
          display: flex;
          gap: 0;
          border-bottom: 1px solid #ddd;
          margin-bottom: 20px;
        }

        .booking-tab {
          border: 0;
          background: transparent;
          padding: 10px 16px;
          cursor: pointer;
          font-size: 14px;
          color: #555;
          border-bottom: 2px solid transparent;
        }

        .booking-tab:hover {
          color: #222;
        }

        .booking-tab.active {
          color: #222;
          font-weight: 600;
          border-bottom-color: #3273dc;
        }

        .booking-tab-content {
          min-height: 500px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #eee;
          border-radius: 8px;
        }

        @media (max-width: 768px) {
          .bookings-calendar {
            height: calc(100vh - 250px);
            min-height: 400px;
          }
          .bookings-calendar .fc-daygrid-day {
            touch-action: manipulation;
          }

          .bookings-calendar .fc-daygrid-day-frame {
            touch-action: manipulation;
          }

          .bookings-calendar .fc-daygrid-day-number {
            min-width: 28px;
            min-height: 28px;
            display: flex;
            align-items: center;
            justify-content: flex-end;
          }

        .bookings-calendar .fc-toolbar {
          flex-wrap: wrap;
          gap: 8px;
        }

          .bookings-calendar .fc-toolbar-title {
            font-size: 17px;
          }

          .bookings-calendar .fc-daygrid-day-number {
            font-size: 12px;
          }

          .bookings-calendar .fc-col-header-cell-cushion {
            font-size: 11px;
          }

          .booking-tab {
            padding: 8px 10px;
            font-size: 13px;
          }
        }
      `}</style>


      <section>

        {/* Page title */}
        <div className="is-title-bar">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0">
            <ul>
              <li>
                <Link
                  to={`/artists?page=${currentPage}`}
                  className="btn btn-primary"
                >
                  Artists
                </Link>
              </li>

              <li>
                Artist Bookings
              </li>
            </ul>
          </div>
        </div>


        {/* Main section */}
        <div className="section main-section">
          <div className="card">

            {/* Card header */}
            <header className="card-header">
              <p className="card-header-title">
                <span className="icon">
                  <i className="mdi mdi-calendar"></i>
                </span>

                Artist Bookings
              </p>
            </header>


            <div className="card-content">

              {/* MAIN TABS */}
              <div className="booking-tabs">

                <button
                  type="button"
                  onClick={() =>
                    setActiveTab("calendar")
                  }
                  className={
                    activeTab === "calendar"
                      ? "booking-tab active"
                      : "booking-tab"
                  }
                >
                  <i className="mdi mdi-calendar"></i>
                  <span>Calendar</span>
                </button>


                <button
                  type="button"
                  onClick={() =>
                    setActiveTab("map")
                  }
                  className={
                    activeTab === "map"
                      ? "booking-tab active"
                      : "booking-tab"
                  }
                >
                  <i className="mdi mdi-map-marker"></i>
                  <span>Map</span>
                </button>


                <button
                  type="button"
                  onClick={() =>
                    setActiveTab("chat")
                  }
                  className={
                    activeTab === "chat"
                      ? "booking-tab active"
                      : "booking-tab"
                  }
                >
                  <i className="mdi mdi-chat"></i>
                  <span>Chat</span>
                </button>

              </div>


              {/* LOADING */}
              {loading &&
                activeTab === "calendar" && (
                  <div className="flex justify-center items-center py-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-solid"></div>
                  </div>
                )}


              {/* ERROR */}
              {!loading &&
                error &&
                activeTab === "calendar" && (
                  <p
                    style={{
                      color: "red",
                      fontWeight: "bold",
                      textAlign: "center"
                    }}
                  >
                    {error}
                  </p>
                )}


              {/* CALENDAR TAB */}
              {!loading &&
                !error &&
                activeTab === "calendar" && (
                  <div className="bookings-calendar">

                    <FullCalendar
                      plugins={[
                        multiMonthPlugin,
                        dayGridPlugin,
                        timeGridPlugin,
                        interactionPlugin,
                        listPlugin
                      ]}

                      /*
                       * IMPORTANT:
                       * Default view is the screenshot-style
                       * single month grid.
                       */
                      initialView="multiMonthYear"
                      firstDay={1}

                      initialDate={new Date()}

                      buttonText={{
                        today: "Today",
                        year: "Year",
                        month: "Month",
                        week: "Week",
                        day: "Day",
                        list: "List"
                      }}

                      /*
                       * Month / Week / Day / List / Year
                       */
                      headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right: "multiMonthYear,dayGridMonth,timeGridWeek,timeGridDay,listYear"
                      }}

                      /*
                       * IMPORTANT:
                       * Month view remains compact and complete.
                       */
                      fixedWeekCount={false}
                      showNonCurrentDates={true}

                      /*
                       * Calendar height is restricted to
                       * the visible area.
                       */
                      height="100%"
                      contentHeight="100%"
                      expandRows={true}

                      /*
                       * Add booking
                       */
                      dayMaxEvents={false}
                      selectable={true}
                      select={handleDateSelect}
                      dateClick={handleDateClick}

                      /*
                       * Edit / Delete booking
                       */
                      editable={false}
                      eventClick={handleEventClick}

                      /*
                       * Database bookings
                       */
                      events={events}

                      /*
                       * Custom booking event
                       */
                      eventContent={(eventInfo) => {
                        const event =
                          eventInfo.event;

                        const description =
                          event.extendedProps
                            ?.description || "";

                        const status =
                          event.extendedProps
                            ?.status ||
                          "confirmed";

                        return (
                          <div
                            className={
                              `fc-custom-event status-${status}`
                            }
                          >
                            <div className="fc-custom-event-title">
                              {event.title}
                            </div>

                            {description && (
                              <div className="fc-custom-event-description">
                                {description}
                              </div>
                            )}
                          </div>
                        );
                      }}

                      /*
                       * When Year view opens,
                       * automatically move to the
                       * current month.
                       */
                      datesSet={(dateInfo) => {
                        if (
                          dateInfo.view.type ===
                          "multiMonthYear"
                        ) {
                          scrollToCurrentMonth();
                        }
                      }}

                      /*
                       * Also run when the view itself
                       * is mounted.
                       */
                      viewDidMount={(viewInfo) => {
                        if (
                          viewInfo.view.type ===
                          "multiMonthYear"
                        ) {
                          scrollToCurrentMonth();
                        }
                      }}
                    />

                  </div>
                )}


              {/* MAP TAB */}
              {activeTab === "map" && (
                <div className="booking-tab-content">
                  <div style={{ textAlign: "center" }}>
                    <i
                      className="mdi mdi-map-marker"
                      style={{
                        fontSize: "50px"
                      }}
                    ></i>

                    <h3>Map View</h3>

                    <p>
                      Artist booking locations will
                      appear here.
                    </p>
                  </div>
                </div>
              )}


              {/* CHAT TAB */}
              {activeTab === "chat" && (
                <div className="booking-tab-content">
                  <div style={{ textAlign: "center" }}>
                    <i
                      className="mdi mdi-chat"
                      style={{
                        fontSize: "50px"
                      }}
                    ></i>

                    <h3>Chat</h3>

                    <p>
                      Artist chat will appear here.
                    </p>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>


        {/* ADD / EDIT / DELETE MODAL */}
        {canManageBookings && (
          <ArtistBookingModal
            isOpen={showBookingModal}
            onClose={handleCloseModal}
            onSaved={handleBookingSaved}
            artistId={artistId}
            selectedDates={selectedDates}
            booking={selectedBooking}
          />
        )}

      </section>
    </>
  );
}


export default Bookings;